import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, AlertCircle, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../contexts/SettingsContext';
import api from '../services/api';
import { clientActionPlanService } from '../services';
import ReportPreviewContent from '../components/ReportPreviewContent';
import { getApiError } from '@/lib/utils';
import { useClientPortal } from '../contexts/ClientPortalContext';

const ClientReportViewPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { apEnabled } = useClientPortal();
  const [report, setReport] = useState(null);
  const [capturedMetrics, setCapturedMetrics] = useState([]);
  const [metricsTemplate, setMetricsTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // Action Plan drilldown — resolved client-side from the already
  // server-scoped list (GET /client/action-plans has no report_id filter;
  // the list itself is already scoped to this user's accessible sites, so
  // finding the match here adds no access beyond what list() already grants).
  const [linkedPlanId, setLinkedPlanId] = useState(undefined); // undefined = not checked yet, null = none found

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const resp = await api.get(`/client/summary-reports/${reportId}`);
        setReport(resp.data);
        
        // Load captured metrics from the response (added by backend)
        const metrics = resp.data.captured_metrics || [];
        if (metrics.length > 0) {
          setCapturedMetrics(metrics);
          setMetricsTemplate({ title: resp.data.metrics_template_title || metrics[0].template_title_snapshot });
        }
      } catch (e) {
        setError(getApiError(e, 'Failed to load report'));
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId]);

  useEffect(() => {
    let cancelled = false;
    clientActionPlanService.list()
      .then(plans => {
        if (cancelled) return;
        const match = (plans || []).find(p => String(p.report_id) === String(reportId));
        setLinkedPlanId(match ? match.id : null);
      })
      .catch(() => { if (!cancelled) setLinkedPlanId(null); });
    return () => { cancelled = true; };
  }, [reportId]);

  const handleExportPdf = async () => {
    setIsExporting(true);
    toast.info('Generating PDF...', { id: 'pdf-export', duration: 30000 });
    try {
      const resp = await api.get(`/client/summary-reports/${reportId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Summary_Report_${reportId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded', { id: 'pdf-export' });
    } catch (e) {
      console.error('PDF download failed:', e);
      toast.error('PDF download failed', { id: 'pdf-export' });
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 mx-auto mb-3 text-red-500" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="client-report-view">
      <div className="mb-4 print:hidden flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        {/* Action Plan link — only shown when AP is enabled for this customer. */}
        {apEnabled === true && linkedPlanId && (
          <Link to={`/client/action-plans/${linkedPlanId}`} data-testid="report-action-plan-link">
            <Button variant="outline" size="sm" className="gap-1.5">
              <ClipboardList className="h-4 w-4" /> Action Plan
            </Button>
          </Link>
        )}
      </div>

      <ReportPreviewContent
        report={report}
        settings={settings}
        showExportPdf={true}
        onExportPdf={handleExportPdf}
        isExporting={isExporting}
        capturedMetrics={capturedMetrics}
        metricsTemplate={metricsTemplate}
      />
    </div>
  );
};

export default ClientReportViewPage;
